"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import {
  Building2,
  Users,
  UserCircle,
  Target,
  Package,
  Megaphone,
  TrendingUp,
  DollarSign,
  Handshake,
  Settings,
  Laptop,
  PiggyBank,
  Compass,
  ChevronLeft,
  Plus,
  PanelRightClose,
  Brain // Import Brain icon
} from "lucide-react"
import { cn } from "@/lib/utils"
import { CollapsedRightRail } from "@/components/rail/collapsed-right-rail"
import type { BrainSelection } from "@/components/brain/brain-page-content"

type ViewState = "domains" | "nodes" | "memories"

interface Domain {
  id: string
  name: string
  icon: typeof Building2
  memoryCount: number
  description: string
}

interface Node {
  id: string
  name: string
  memoryCount: number
  description: string
}

interface Memory {
  id: string
  label: string
  value: string
  confidence: "high" | "medium" | "low"
}

interface BrainRailProps {
  selection?: BrainSelection
  onSelectionChange?: (selection: BrainSelection) => void
}

const PLACEHOLDER_DOMAINS: Domain[] = [
  { id: "identity", name: "Identity", icon: Building2, memoryCount: 0, description: "Who we are, why we exist" },
  { id: "team", name: "Team", icon: Users, memoryCount: 0, description: "Who leads and executes" },
  { id: "audience", name: "Audience", icon: UserCircle, memoryCount: 0, description: "Who we serve" },
  { id: "competition", name: "Competition", icon: Target, memoryCount: 0, description: "Who else is in the market" },
  { id: "offerings", name: "Offerings", icon: Package, memoryCount: 0, description: "What we deliver" },
  { id: "brand", name: "Brand", icon: Megaphone, memoryCount: 0, description: "How we are perceived" },
  { id: "marketing", name: "Marketing", icon: TrendingUp, memoryCount: 0, description: "How we reach people" },
  { id: "sales", name: "Sales", icon: DollarSign, memoryCount: 0, description: "How we convert and close" },
  {
    id: "partnerships",
    name: "Partnerships",
    icon: Handshake,
    memoryCount: 0,
    description: "Who we work with strategically",
  },
  { id: "operations", name: "Operations", icon: Settings, memoryCount: 0, description: "How we deliver" },
  { id: "technology", name: "Technology", icon: Laptop, memoryCount: 0, description: "What systems enable us" },
  { id: "finance", name: "Finance", icon: PiggyBank, memoryCount: 0, description: "Our financial reality" },
  { id: "strategy", name: "Strategy", icon: Compass, memoryCount: 0, description: "Where we are going" },
]

const PLACEHOLDER_NODES: Record<string, Node[]> = {
  identity: [
    {
      id: "basics",
      name: "Profile", // NOT "Company Profile"
      memoryCount: 0,
      description: "Legal name, preferred name, org type, industry", // NO locations
    },
    { id: "mission", name: "Mission", memoryCount: 0, description: "Why we exist" },
    { id: "values", name: "Values", memoryCount: 0, description: "What we optimize for" },
    { id: "model", name: "Model", memoryCount: 0, description: "How value is created and captured" }, // NOT "Business Model"
    { id: "category", name: "Category", memoryCount: 0, description: "What archetype we occupy" },
    { id: "origin", name: "Origin", memoryCount: 0, description: "Founding story and context" },
  ],
  team: [
    { id: "leadership", name: "Leadership", memoryCount: 0, description: "Key decision-makers and governance" },
    { id: "structure", name: "Structure", memoryCount: 0, description: "Roles, functions, accountability" },
    { id: "capacity", name: "Capacity", memoryCount: 0, description: "Available bandwidth and constraints" },
    { id: "capabilities", name: "Capabilities", memoryCount: 0, description: "Team strengths" },
    { id: "gaps", name: "Gaps", memoryCount: 0, description: "Capability gaps and hiring needs" },
    { id: "norms", name: "Norms", memoryCount: 0, description: "Working norms and patterns" },
    { id: "advisors", name: "Advisors", memoryCount: 0, description: "Board, advisors, key volunteers" },
  ],
  audience: [
    { id: "segments", name: "Segments", memoryCount: 0, description: "Customer or beneficiary segments" },
    { id: "needs", name: "Needs", memoryCount: 0, description: "Pains, aspirations, jobs-to-be-done" },
    { id: "triggers", name: "Triggers", memoryCount: 0, description: "What causes them to act" },
    { id: "hesitations", name: "Hesitations", memoryCount: 0, description: "Why they hesitate pre-conversion" },
    { id: "retention", name: "Retention", memoryCount: 0, description: "Relationship health and churn" },
    { id: "feedback", name: "Feedback", memoryCount: 0, description: "Reviews, interviews, NPS insights" },
    { id: "beneficiaries", name: "Beneficiaries", memoryCount: 0, description: "Population served (nonprofit)" },
  ],
  competition: [
    { id: "competitors", name: "Competitors", memoryCount: 0, description: "Direct competitors" },
    { id: "alternatives", name: "Alternatives", memoryCount: 0, description: "Indirect alternatives and substitutes" },
    { id: "comparisons", name: "Comparisons", memoryCount: 0, description: "Head-to-head differentiation" },
    { id: "trends", name: "Trends", memoryCount: 0, description: "Market and industry shifts" },
    { id: "benchmarks", name: "Benchmarks", memoryCount: 0, description: "Industry benchmarks" },
    { id: "watchlist", name: "Watchlist", memoryCount: 0, description: "Competitor moves to track" },
  ],
  offerings: [
    { id: "catalog", name: "Catalog", memoryCount: 0, description: "Products, services, programs" },
    { id: "pricing", name: "Pricing", memoryCount: 0, description: "Pricing structure and tiers" },
    { id: "outcomes", name: "Outcomes", memoryCount: 0, description: "Results and value delivered" },
    { id: "differentiators", name: "Differentiators", memoryCount: 0, description: "What makes offerings unique" },
    { id: "requirements", name: "Requirements", memoryCount: 0, description: "Prerequisites to deliver" },
    { id: "guarantees", name: "Guarantees", memoryCount: 0, description: "Quality standards and SLAs" },
    { id: "roadmap", name: "Roadmap", memoryCount: 0, description: "Strategic offering evolution" },
  ],
  brand: [
    { id: "promise", name: "Promise", memoryCount: 0, description: "Brand promise and positioning" },
    { id: "messaging", name: "Messaging", memoryCount: 0, description: "Messaging pillars and narratives" },
    { id: "voice", name: "Voice", memoryCount: 0, description: "Voice and tone guidelines" },
    { id: "visuals", name: "Visuals", memoryCount: 0, description: "Visual identity principles" },
    { id: "proof", name: "Proof", memoryCount: 0, description: "Case studies and credibility" },
    { id: "reputation", name: "Reputation", memoryCount: 0, description: "Brand sentiment and perception" },
  ],
  marketing: [
    { id: "channels", name: "Channels", memoryCount: 0, description: "Channel strategy and mix" },
    { id: "content", name: "Content", memoryCount: 0, description: "Content strategy and themes" },
    { id: "campaigns", name: "Campaigns", memoryCount: 0, description: "Time-bound campaigns" },
    { id: "seo", name: "SEO", memoryCount: 0, description: "Search visibility and keywords" },
    { id: "paid-media", name: "Paid Media", memoryCount: 0, description: "Paid acquisition motions" },
    { id: "nurture", name: "Nurture", memoryCount: 0, description: "Lifecycle marketing" },
    { id: "learnings", name: "Learnings", memoryCount: 0, description: "Channel performance insights" },
  ],
  sales: [
    { id: "pipeline", name: "Pipeline", memoryCount: 0, description: "Sales motion and stages" },
    { id: "qualification", name: "Qualification", memoryCount: 0, description: "ICP fit tests and disqualifiers" },
    { id: "objections", name: "Objections", memoryCount: 0, description: "Objections and talk tracks" },
    { id: "deals", name: "Deals", memoryCount: 0, description: "Deal mechanics and proposals" },
    { id: "forecast", name: "Forecast", memoryCount: 0, description: "Pipeline health and KPIs" },
    { id: "enablement", name: "Enablement", memoryCount: 0, description: "Sales enablement assets" },
    { id: "renewals", name: "Renewals", memoryCount: 0, description: "Renewal and expansion motions" },
  ],
  partnerships: [
    { id: "referrals", name: "Referrals", memoryCount: 0, description: "Referral sources and networks" },
    { id: "alliances", name: "Alliances", memoryCount: 0, description: "Strategic alliances" },
    { id: "resellers", name: "Resellers", memoryCount: 0, description: "Channel partners" },
    { id: "collaborations", name: "Collaborations", memoryCount: 0, description: "Co-marketing arrangements" },
    { id: "grantmakers", name: "Grantmakers", memoryCount: 0, description: "Institutional funding (nonprofit)" },
    { id: "strategic-vendors", name: "Strategic Vendors", memoryCount: 0, description: "Non-substitutable vendors" },
    { id: "stakeholders", name: "Stakeholders", memoryCount: 0, description: "Ecosystem influencers" },
  ],
  operations: [
    { id: "workflows", name: "Workflows", memoryCount: 0, description: "Delivery workflows and playbooks" },
    { id: "onboarding", name: "Onboarding", memoryCount: 0, description: "Customer onboarding experience" },
    { id: "bottlenecks", name: "Bottlenecks", memoryCount: 0, description: "Capacity constraints" },
    { id: "standards", name: "Standards", memoryCount: 0, description: "Quality standards and SOPs" },
    { id: "vendors", name: "Vendors", memoryCount: 0, description: "Operational vendor management" },
    { id: "support", name: "Support", memoryCount: 0, description: "Support and issue resolution" },
    { id: "compliance", name: "Compliance", memoryCount: 0, description: "Operational compliance" },
  ],
  technology: [
    { id: "stack", name: "Stack", memoryCount: 0, description: "Core technology and tools" },
    { id: "web-analytics", name: "Web Analytics", memoryCount: 0, description: "Web presence and tracking" },
    { id: "integrations", name: "Integrations", memoryCount: 0, description: "System integrations" },
    { id: "data-systems", name: "Data Systems", memoryCount: 0, description: "Data infrastructure" },
    { id: "security", name: "Security", memoryCount: 0, description: "Security posture" },
    { id: "tech-debt", name: "Tech Debt", memoryCount: 0, description: "Technical debt and upgrades" },
  ],
  finance: [
    { id: "revenue", name: "Revenue", memoryCount: 0, description: "Revenue and funding streams" },
    { id: "costs", name: "Costs", memoryCount: 0, description: "Cost structure and margins" },
    { id: "cash-flow", name: "Cash Flow", memoryCount: 0, description: "Cash flow and runway" },
    { id: "budgets", name: "Budgets", memoryCount: 0, description: "Budget guardrails" },
    { id: "unit-economics", name: "Unit Economics", memoryCount: 0, description: "Unit economics" },
    { id: "commitments", name: "Commitments", memoryCount: 0, description: "Financial commitments" },
    { id: "reporting", name: "Reporting", memoryCount: 0, description: "Financial reporting cadence" },
  ],
  strategy: [
    { id: "direction", name: "Direction", memoryCount: 0, description: "Strategic vision and bets" },
    { id: "principles", name: "Principles", memoryCount: 0, description: "Non-negotiable decision rules" },
    { id: "decisions", name: "Decisions", memoryCount: 0, description: "Major decisions with rationale" },
    { id: "assumptions", name: "Assumptions", memoryCount: 0, description: "Key beliefs strategy depends on" },
    { id: "risks", name: "Risks", memoryCount: 0, description: "Strategic threats" },
    { id: "opportunities", name: "Opportunities", memoryCount: 0, description: "Strategic upside" },
    { id: "constraints", name: "Constraints", memoryCount: 0, description: "Hard limits and blockers" },
    { id: "wins", name: "Wins", memoryCount: 0, description: "Completed goals and milestones" },
    {
      id: "theory-change",
      name: "Theory of Change",
      memoryCount: 0,
      description: "Activities to impact model (nonprofit)",
    },
  ],
}

const PLACEHOLDER_MEMORIES: Record<string, Memory[]> = {}

function getBrainRailOpenState(): boolean {
  if (typeof window === "undefined") return true
  try {
    const value = localStorage.getItem("logosiq.brainRailOpen")
    return value === null ? true : value === "true"
  } catch {
    return true
  }
}

function setBrainRailOpenState(isOpen: boolean): void {
  if (typeof window === "undefined") return
  try {
    localStorage.setItem("logosiq.brainRailOpen", isOpen.toString())
  } catch {
    // Silently fail
  }
}

export function BrainRail({ selection, onSelectionChange }: BrainRailProps) {
  const [viewState, setViewState] = useState<ViewState>("domains")
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null)
  const [selectedNode, setSelectedNode] = useState<Node | null>(null)
  const [railOpen, setRailOpen] = useState<boolean>(true)

  useEffect(() => {
    const storedState = getBrainRailOpenState()
    setRailOpen(storedState)
  }, [])

  useEffect(() => {
    if (!selection) return

    if (selection.type === "domain") {
      const domain = PLACEHOLDER_DOMAINS.find((d) => d.id === selection.slug)
      if (domain) {
        setSelectedDomain(domain)
        setViewState("nodes")
      }
    } else if (selection.type === "node") {
      const domain = PLACEHOLDER_DOMAINS.find((d) => d.id === selection.domainSlug)
      if (domain) {
        setSelectedDomain(domain)
        const node = PLACEHOLDER_NODES[domain.id]?.find((n) => n.id === selection.nodeSlug)
        if (node) {
          setSelectedNode(node)
          setViewState("memories")
        } else {
          setViewState("nodes")
        }
      }
    } else if (selection.type === "overview") {
      setSelectedDomain(null)
      setSelectedNode(null)
      setViewState("domains")
    }
  }, [selection])

  const handleOpenRail = () => {
    setRailOpen(true)
    setBrainRailOpenState(true)
  }

  const handleCloseRail = () => {
    setRailOpen(false)
    setBrainRailOpenState(false)
  }

  const handleDomainClick = (domain: Domain) => {
    setSelectedDomain(domain)
    setViewState("nodes")
    onSelectionChange?.({ type: "domain", slug: domain.id, label: domain.name })
  }

  const handleNodeClick = (node: Node) => {
    setSelectedNode(node)
    setViewState("memories")
    if (selectedDomain) {
      onSelectionChange?.({
        type: "node",
        domainSlug: selectedDomain.id,
        nodeSlug: node.id,
        label: node.name,
      })
    }
  }

  const handleBackToDomains = () => {
    setSelectedDomain(null)
    setSelectedNode(null)
    setViewState("domains")
    onSelectionChange?.({ type: "overview" })
  }

  const handleBackToNodes = () => {
    setSelectedNode(null)
    setViewState("nodes")
    if (selectedDomain) {
      onSelectionChange?.({ type: "domain", slug: selectedDomain.id, label: selectedDomain.name })
    }
  }

  const currentNodes = selectedDomain ? PLACEHOLDER_NODES[selectedDomain.id] || [] : []
  const currentMemories =
    selectedDomain && selectedNode ? PLACEHOLDER_MEMORIES[`${selectedDomain.id}-${selectedNode.id}`] || [] : []

  const totalMemoriesCount = PLACEHOLDER_DOMAINS.reduce((sum, d) => sum + d.memoryCount, 0)

  if (!railOpen) {
    return <CollapsedRightRail onOpen={handleOpenRail} badgeCount={totalMemoriesCount} ariaLabel="Open Memory panel" />
  }

  const headerTitle =
    viewState === "domains" ? "Memories" : viewState === "nodes" ? selectedDomain?.name : selectedNode?.name

  return (
    <aside
      className={cn(
        "fixed lg:static",
        "inset-y-0 right-0 z-50 lg:z-0",
        "flex flex-col",
        "lg:flex",
        railOpen ? "w-80 lg:w-[340px]" : "lg:w-0 lg:opacity-0 lg:pointer-events-none",
        "min-h-full",
        "bg-background lg:bg-[var(--surface-context)]",
        railOpen && "lg:shadow-[inset_4px_0_8px_rgba(0,0,0,0.18)]",
      )}
      style={{ flexShrink: 0 }}
    >
      {/* Header - Brain icon matches capital letter height of "Memories" */}
      <div className="hidden lg:flex h-16 items-center justify-between px-4 pb-1 shrink-0">
        <div className="flex items-center gap-2">
          {/* Brain icon - elegant 2px stroke, no background, sized to match cap height (~22px) */}
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-[var(--brain)]"
          >
            {/* Brain outline - left hemisphere */}
            <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
            {/* Brain outline - right hemisphere */}
            <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" />
            {/* Internal folds */}
            <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4" />
            <path d="M12 18v-3" />
          </svg>
          <span className="text-[22px] font-display font-normal text-foreground">
            {viewState === "domains" ? "Memories" : headerTitle}
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-lg"
          onClick={handleCloseRail}
          aria-label="Close memory panel"
        >
          <PanelRightClose className="h-4 w-4 text-[var(--text-secondary)]" />
        </Button>
      </div>

      {/* Navigation sub-header for drilldown states */}
      {viewState !== "domains" && (
        <div className="hidden lg:flex h-10 items-center justify-between px-4 shrink-0 border-b border-[var(--border)]">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={viewState === "nodes" ? handleBackToDomains : handleBackToNodes}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">
            {viewState === "nodes" && "Domains"}
            {viewState === "memories" && selectedDomain?.name}
          </span>
          {viewState === "memories" ? (
            <Button variant="default" size="sm" className="h-8 gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              Add
            </Button>
          ) : (
            <div className="w-8" />
          )}
        </div>
      )}

      {/* Scrollable content */}
      <div className="flex-1 overflow-hidden min-h-0">
        <div className="h-full overflow-y-auto p-4 scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {/* Domains view */}
          {viewState === "domains" && (
            <div className="space-y-2">
              {PLACEHOLDER_DOMAINS.map((domain) => {
                const Icon = domain.icon
                const isSelected = selection?.type === "domain" && selection.slug === domain.id
                return (
                  <button
                    key={domain.id}
                    onClick={() => handleDomainClick(domain)}
                    className={cn(
                      "w-full flex items-center justify-between p-3 rounded-lg",
                      "border border-[var(--border)]",
                      isSelected ? "bg-primary/10 border-primary/30" : "bg-[var(--surface)]",
                      "hover:bg-[var(--surface-hover)]",
                      "transition-colors duration-200",
                      "text-left",
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5 text-muted-foreground" />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-foreground">{domain.name}</span>
                        <span className="text-xs text-muted-foreground">{domain.description}</span>
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0 ml-2">
                      {domain.memoryCount === 0 ? "—" : `${domain.memoryCount}`}
                    </span>
                  </button>
                )
              })}
            </div>
          )}

          {/* Nodes view */}
          {viewState === "nodes" && (
            <div className="space-y-2">
              {currentNodes.map((node) => {
                const isSelected =
                  selection?.type === "node" &&
                  selection.domainSlug === selectedDomain?.id &&
                  selection.nodeSlug === node.id
                return (
                  <button
                    key={node.id}
                    onClick={() => handleNodeClick(node)}
                    className={cn(
                      "w-full flex items-center justify-between p-3 rounded-lg",
                      "border border-[var(--border)]",
                      isSelected ? "bg-primary/10 border-primary/30" : "bg-[var(--surface)]",
                      "hover:bg-[var(--surface-hover)]",
                      "transition-colors duration-200",
                      "text-left",
                    )}
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-foreground">{node.name}</span>
                      <span className="text-xs text-muted-foreground">{node.description}</span>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0 ml-2">
                      {node.memoryCount === 0 ? "—" : `${node.memoryCount}`}
                    </span>
                  </button>
                )
              })}
            </div>
          )}

          {/* Memories view */}
          {viewState === "memories" && (
            <div className="space-y-3">
              {currentMemories.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  <p>No memories yet.</p>
                  <p className="mt-2 text-xs">Add your first memory to this node.</p>
                </div>
              ) : (
                currentMemories.map((memory) => (
                  <div
                    key={memory.id}
                    className={cn(
                      "p-3 rounded-lg",
                      "border border-[var(--border)]",
                      "bg-[var(--surface)]",
                      "hover:bg-[var(--surface-hover)]",
                      "transition-colors duration-200",
                      "cursor-pointer",
                    )}
                    onClick={() => onSelectionChange?.({ type: "memory", id: memory.id, title: memory.label })}
                  >
                    <p className="text-sm font-medium text-foreground">{memory.label}</p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{memory.value}</p>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </aside>
  )
}
